/**
 * Date formatting utilities for displaying dates in user's local timezone
 * All dates from the backend are in ISO 8601 format (UTC)
 */

/**
 * Format ISO date string to localized date and time
 * @param {string} isoString - ISO 8601 date string (e.g., "2024-11-10T14:30:45.000Z")
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in user's local timezone
 */
export function formatDateTime(isoString, options = {}) {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    return "";
  }

  const defaultOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...options,
  };

  return new Intl.DateTimeFormat("en-US", defaultOptions).format(date);
}

/**
 * Format ISO date string for order dates: D/M/YYYY H:MM AM/PM
 * Primary date formatter for displaying order timestamps
 * @param {string} isoString - ISO 8601 date string
 * @returns {string} Formatted date string in user's local timezone
 */
export function formatOrderDate(isoString) {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    return "";
  }

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const minutesStr = minutes < 10 ? "0" + minutes : minutes;

  return `${day}/${month}/${year} ${hours}:${minutesStr} ${ampm}`;
}

/**
 * Format ISO date string to short date only
 * @param {string} isoString - ISO 8601 date string
 * @returns {string} Formatted date (e.g., "11/10/2024")
 */
export function formatDateOnly(isoString) {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).format(date);
}

/**
 * Format ISO date string to time only
 * @param {string} isoString - ISO 8601 date string
 * @returns {string} Formatted time (e.g., "2:30 PM")
 */
export function formatTimeOnly(isoString) {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
}

/**
 * Get relative time string (e.g., "2 hours ago", "just now")
 * @param {string} isoString - ISO 8601 date string
 * @returns {string} Relative time string
 */
export function getRelativeTime(isoString) {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    return "";
  }

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

  return formatDateOnly(isoString);
}
